import React, { useState } from 'react';
import AdminService from '../../../services/AdminService';
import './FixAuth0Modal.css';

/**
 * Manual Auth0 repair for one user, wired to POST /admin/reconcile-auth0.
 *
 * Reached three ways: from a relink that came back needs_manual/error, from an
 * email-mismatch flag (initialOldEmail pre-filled), or standalone from the users
 * list when someone reports they can't log. User reports are the intended discovery
 * path for Auth0-side breakage.
 *
 * Submitting with no options is safe to repeat: when the account is healthy the
 * backend only reads ('already_linked'). There is deliberately no password
 * input, a created identity gets an unshared generated password and the user
 * completes a normal password reset.
 *
 * @returns {React.JSX.Element}
 */
export const FixAuth0Modal = ({ userId, userEmail, initialOldEmail, onClose }) => {
  const [oldEmail, setOldEmail] = useState(initialOldEmail || '');
  const [createIfMissing, setCreateIfMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  // auth0 object from the last response; null until a submit completes.
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // These three mean the account is now (or already was) healthy; the other two
  // ('needs_manual', 'ambiguous') come back as 200 but nothing was fixed, so the
  // form stays available.
  const resolved = ['already_linked', 'email_updated', 'created'].includes(result?.status);

  const submit = async () => {
    if (createIfMissing && !window.confirm(
      'Create a brand-new Auth0 identity for this user? Only do this if you are '
      + 'sure none exists under a previous email. otherwise you will strand the '
      + 'old identity and duplicate the account.'
    )) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const trimmed = oldEmail.trim();
      const response = await AdminService.reconcileAuth0({
        userId,
        oldEmail: trimmed || undefined,
        createIfMissing: createIfMissing || undefined,
      });
      setResult(response.auth0);
    } catch (e) {
      setError(e.message || 'Reconcile failed');
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fix-auth0-overlay" onClick={onClose}>
      <div className="fix-auth0-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fix-auth0-header">
          <h3>Fix Auth</h3>
          <button className="fix-auth0-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <dl className="fix-auth0-meta">
          <dt>User</dt>
          <dd>{userEmail || '-'} (id {userId})</dd>
        </dl>

        {error && <p className="fix-auth0-notice fix-auth0-notice-error">{error}</p>}

        {result?.status === 'already_linked' && (
          <div className="fix-auth0-notice fix-auth0-notice-success">
            <strong>Nothing to fix.</strong> An Auth0 identity already exists for this
            user's current email{result.authUserId ? <> (<code>{result.authUserId}</code>)</> : null}.
            Login and password reset work.
          </div>
        )}

        {result?.status === 'email_updated' && (
          <div className="fix-auth0-notice fix-auth0-notice-success">
            <strong>Identity migrated.</strong> The Auth0 identity was moved from the
            previous email onto the current one. The user now logs in with{' '}
            <code>{userEmail}</code>, and their existing password still works.
          </div>
        )}

        {result?.status === 'created' && (
          <div className="fix-auth0-notice fix-auth0-notice-success">
            <strong>Identity created.</strong>
            {result.passwordResetRequired && (
              <p className="fix-auth0-reset-instruction">
                The user must run <strong>"Forgot password"</strong> before their first
                login. The generated password is not shown to anyone. Tell
                the user to request a reset for <code>{userEmail}</code>.
              </p>
            )}
          </div>
        )}

        {result?.status === 'ambiguous' && (
          <div className="fix-auth0-notice fix-auth0-notice-warn">
            <strong>Refusing to guess.</strong> {result.detail}
            <p>
              Multiple identities sit under that previous email. Decide which one is
              real in the Auth0 dashboard (last-login is usually the tell), remove or
              rename the others, then run this again.
            </p>
          </div>
        )}

        {result?.status === 'needs_manual' && (
          <div className="fix-auth0-notice fix-auth0-notice-warn">
            <strong>No identity found.</strong> {result.detail}
            <p>
              Nothing exists under the current email{oldEmail.trim() ? ' or the previous email you supplied' : ''}.
              If you know the address the identity might be filed under, enter it below;
              otherwise tick "create a new identity".
            </p>
          </div>
        )}

        {!resolved && (
          <>
            <p className="fix-auth0-explain">
              Checks for an Auth0 identity under the user's current email. With no
              options set this is a safe, read-only check when the account is healthy.
            </p>
            <label className="fix-auth0-field">
              <span>Previous email (optional)</span>
              <input
                type="email"
                value={oldEmail}
                onChange={(e) => setOldEmail(e.target.value)}
                disabled={busy}
                placeholder="address the identity may be filed under"
              />
            </label>
            <p className="fix-auth0-explain">
              Supply this whenever you suspect the identity survives under a pre-merge
              address. Migrating it keeps the user's password; creating a fresh one
              does not.
            </p>
            <label className="fix-auth0-checkbox">
              <input
                type="checkbox"
                checked={createIfMissing}
                onChange={(e) => setCreateIfMissing(e.target.checked)}
                disabled={busy}
              />
              <span>Create a new identity if none is found</span>
            </label>
            <button
              className={`cta-button width-100 ${busy ? 'disabled-btn' : ''}`}
              disabled={busy}
              onClick={submit}
            >
              {busy ? 'Working… (Auth0 calls can take a few seconds)' : 'Run reconcile'}
            </button>
          </>
        )}

        <div className="fix-auth0-footer">
          <button className="cta-button" onClick={onClose} disabled={busy}>
            {resolved ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FixAuth0Modal;
